import { FlightDto, FlightLevelBand, TimePeriod, TvRefLocation } from 'app/model/CORE';
import {
    CoordinationSummary,
    ExtendedMonitoredTv,
    MonitoredMcdmMeasure,
    MonitoredMeasure,
    MonitoredRegulationOutlook,
} from 'app/model/SOL49';
import { MonitoredFlow } from 'app/model/client/CORE';
import AppContext from '../context/AppContext';
import Utils from '../util/Utils';
import { ReactElement } from 'react';
import { MapClickElement } from 'app/components/map/util/MapUtils';
import { MapLayer, MapObjectType } from 'app/components/dashboard/network/map/Map';
import { EntityType, MonitoringPeriodSettings } from 'app/model/client/SOL49';
import { DashboardSettings } from 'app/components/panels/settings/SettingsPanel';
import { MonitoredEventType } from 'app/common/util/MonitoredEventUtils';

export class BusEvent {
    // It is required to set eventName property, since in production build there's no way to access classname (even via constructor.name for example)
    // Event name shall be different for each event
    eventName: string;

    constructor(eventName: string) {
        this.eventName = eventName;
    }
}

export class ShowModalEvent extends BusEvent {
    static NAME = 'ShowModal';
    content: ReactElement;
    width: string;
    positionLeft: number | string;
    positionTop: number | string;
    closable: boolean;

    constructor(
        eventName: string,
        content?: ReactElement,
        width?: string,
        positionLeft?: number | string,
        positionTop?: number | string,
        closable?: boolean,
    ) {
        super(eventName);
        this.content = content;
        this.width = width;
        this.positionLeft = positionLeft;
        this.positionTop = positionTop;
        this.closable = closable;
    }

    static fire(content: ReactElement, width: string, positionLeft?: number, positionTop?: number) {
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, width, positionLeft, positionTop));
    }

    static fireNotClosable(content: ReactElement, width: string, positionLeft?: number, positionTop?: number) {
        AppContext.eventBus.publish(
            new ShowModalEvent(ShowModalEvent.NAME, content, width, positionLeft, positionTop, false),
        );
    }
}

export class MapAvailableEvent extends BusEvent {
    static NAME = 'MapAvailableEvent';
    mapId: number;

    constructor(mapId: number) {
        super(MapAvailableEvent.NAME);
        this.mapId = mapId;
    }

    static fire(mapId: number) {
        AppContext.eventBus.publish(new MapAvailableEvent(mapId));
    }
}

export class MapUnavailableEvent extends BusEvent {
    static NAME = 'MapUnavailableEvent';
    mapId: number;

    constructor(mapId: number) {
        super(MapUnavailableEvent.NAME);
        this.mapId = mapId;
    }

    static fire(mapId: number) {
        AppContext.eventBus.publish(new MapUnavailableEvent(mapId));
    }
}

export class ShowMapLayerEvent extends BusEvent {
    static NAME = 'ShowMapLayerEvent';
    layer: MapLayer;
    objects: any[];
    timePeriod: TimePeriod;
    flightLevelBand: FlightLevelBand;
    mapId: number;

    constructor(
        eventName: string,
        mapId: number,
        layer: MapLayer,
        objects: any[],
        timePeriod: TimePeriod,
        flightLevelBand: FlightLevelBand,
    ) {
        super(eventName);
        this.layer = layer;
        this.objects = objects;
        this.timePeriod = timePeriod;
        this.flightLevelBand = flightLevelBand;
        this.mapId = mapId;
    }

    static fire(
        mapId: number,
        layer: MapLayer,
        objects: any[],
        timePeriod: TimePeriod,
        flightLevelBand: FlightLevelBand,
    ) {
        const event = new ShowMapLayerEvent(ShowMapLayerEvent.NAME, mapId, layer, objects, timePeriod, flightLevelBand);
        AppContext.eventBus.publish(event);
    }
}

export class ClearMapLayerEvent extends BusEvent {
    static NAME = 'ClearMapLayerEvent';
    layer: MapLayer;
    mapId: number;

    constructor(eventName: string, mapId: number, layer: MapLayer) {
        super(eventName);
        this.layer = layer;
        this.mapId = mapId;
    }

    static fire(mapId: number, layer: MapLayer) {
        AppContext.eventBus.publish(new ClearMapLayerEvent(ClearMapLayerEvent.NAME, mapId, layer));
    }
}

export class LayerUpdateEvent extends BusEvent {
    static NAME = 'LayerUpdateEvent';
    layer: MapLayer;
    mapId: number;

    constructor(eventName: string, mapId: number, layer: MapLayer) {
        super(eventName);
        this.layer = layer;
        this.mapId = mapId;
    }

    static fire(mapId: number, layer: MapLayer) {
        AppContext.eventBus.publish(new LayerUpdateEvent(LayerUpdateEvent.NAME, mapId, layer));
    }
}

export class NewCustomLayerEvent extends BusEvent {
    static NAME = 'NewCustomLayerEvent';
    layerName: string;
    objectsKeyPerType: Map<MapObjectType, Array<string>>;

    constructor(eventName: string, layerName: string, objectsKeyPerType: Map<MapObjectType, Array<string>>) {
        super(eventName);
        this.layerName = layerName;
        this.objectsKeyPerType = objectsKeyPerType;
    }

    static fire(layerName: string, objectsKeyPerType: Map<MapObjectType, Array<string>>) {
        AppContext.eventBus.publish(new NewCustomLayerEvent(NewCustomLayerEvent.NAME, layerName, objectsKeyPerType));
    }
}

export class DeleteCustomLayerEvent extends BusEvent {
    static NAME = 'DeleteCustomLayerEvent';
    layer: MapLayer;

    constructor(eventName: string, layer: MapLayer) {
        super(eventName);
        this.layer = layer;
    }

    static fire(layer: MapLayer) {
        AppContext.eventBus.publish(new DeleteCustomLayerEvent(DeleteCustomLayerEvent.NAME, layer));
    }
}

export class AddToCustomLayerEvent extends BusEvent {
    static NAME = 'AddToCustomLayerEvent';
    layer: MapLayer;
    objectKey: string;
    objectType: MapObjectType;

    constructor(eventName: string, layer: MapLayer, objectKey: string, objectType: MapObjectType) {
        super(eventName);
        this.layer = layer;
        this.objectKey = objectKey;
        this.objectType = objectType;
    }

    static fire(layer: MapLayer, objectKey: string, objectType: MapObjectType) {
        AppContext.eventBus.publish(
            new AddToCustomLayerEvent(AddToCustomLayerEvent.NAME, layer, objectKey, objectType),
        );
    }
}

export class AddToWorkspaceLayerEvent extends BusEvent {
    static NAME = 'AddToWorkspaceLayerEvent';
    objects: Map<MapObjectType, Array<string>>;

    constructor(eventName: string, objects: Map<MapObjectType, Array<string>>) {
        super(eventName);
        this.objects = objects;
    }

    static fire(objects: Map<MapObjectType, Array<string>>) {
        AppContext.eventBus.publish(new AddToWorkspaceLayerEvent(AddToWorkspaceLayerEvent.NAME, objects));
    }
}

export class RemoveFromWorkspaceLayerEvent extends BusEvent {
    static NAME = 'RemoveFromWorkspaceLayerEvent';
    objects: Map<MapObjectType, Array<string>>;

    constructor(eventName: string, objects: Map<MapObjectType, Array<string>>) {
        super(eventName);
        this.objects = objects;
    }

    static fire(objects: Map<MapObjectType, Array<string>>) {
        AppContext.eventBus.publish(new RemoveFromWorkspaceLayerEvent(RemoveFromWorkspaceLayerEvent.NAME, objects));
    }

    static fireRemoveTv(tvName: string) {
        const map: Map<MapObjectType, Array<string>> = new Map();
        map.set(MapObjectType.TV, [tvName]);
        this.fire(map);
    }

    static fireRemoveMeasure(measureId: string) {
        const map: Map<MapObjectType, Array<string>> = new Map();
        map.set(MapObjectType.MEASURE, [measureId]);
        this.fire(map);
    }
}

export class RemoveFromCustomLayerEvent extends BusEvent {
    static NAME = 'RemoveFromCustomLayerEvent';
    layer: MapLayer;
    objectKey: string;
    objectType: MapObjectType;

    constructor(eventName: string, layer: MapLayer, objectKey: string, objectType: MapObjectType) {
        super(eventName);
        this.layer = layer;
        this.objectKey = objectKey;
        this.objectType = objectType;
    }

    static fire(layer: MapLayer, objectKey: string, objectType: MapObjectType) {
        AppContext.eventBus.publish(
            new RemoveFromCustomLayerEvent(RemoveFromCustomLayerEvent.NAME, layer, objectKey, objectType),
        );
    }
}

export class MapElementClickEvent extends BusEvent {
    static NAME = 'MapSelectionEvent';
    selection: Array<MapClickElement>;
    rightClick: boolean;
    altKey: boolean;

    clientXY: number[];

    constructor(
        eventName: string,
        selection: Array<MapClickElement>,
        clientXY: number[],
        altKey: boolean,
        rightClick?: boolean,
    ) {
        super(eventName);
        this.selection = selection;
        this.clientXY = clientXY;
        this.altKey = altKey;
        this.rightClick = rightClick;
    }

    static fireClick(selection: Array<MapClickElement>, clientXY?: number[]) {
        AppContext.eventBus.publish(
            new MapElementClickEvent(MapElementClickEvent.NAME, selection, clientXY, false, false),
        );
    }

    static fireClickWithAltKey(selection: Array<MapClickElement>, clientXY?: number[]) {
        AppContext.eventBus.publish(
            new MapElementClickEvent(MapElementClickEvent.NAME, selection, clientXY, true, false),
        );
    }
    static fireRightClick(selection: Array<MapClickElement>, clientXY?: number[]) {
        AppContext.eventBus.publish(
            new MapElementClickEvent(MapElementClickEvent.NAME, selection, clientXY, false, true),
        );
    }
}

export class ShowTvDetailEvent extends BusEvent {
    static NAME = 'ShowTvDetailEvent';
    tv: string;

    constructor(eventName: string, tv: string) {
        super(eventName);
        this.tv = tv;
    }

    static fireEvent(tv: string) {
        AppContext.eventBus.publish(new ShowTvDetailEvent(ShowTvDetailEvent.NAME, tv));
    }
}

class UpdateSelection<T> extends BusEvent {
    action: 'add' | 'replace';
    selection: Array<T>;

    constructor(eventName: string, action: 'add' | 'replace', selection: Array<T>) {
        super(eventName);
        this.action = action;
        this.selection = selection;
    }
}

export class UpdateFlightSelection extends UpdateSelection<FlightDto> {
    static NAME = 'UpdateFlightSelection';

    constructor(action: 'add' | 'replace', selection: Array<FlightDto>) {
        super(UpdateFlightSelection.NAME, action, selection);
    }
}

export class UpdateMeasureSelection extends UpdateSelection<MonitoredMeasure> {
    static NAME = 'UpdateMeasureSelection';

    constructor(action: 'add' | 'replace', selection: Array<MonitoredMeasure>) {
        super(UpdateMeasureSelection.NAME, action, selection);
    }
}

export class UpdateHotspotSelection extends UpdateSelection<ExtendedMonitoredTv> {
    static NAME = 'UpdateHotspotSelection';

    constructor(action: 'add' | 'replace', selection: Array<ExtendedMonitoredTv>) {
        super(UpdateHotspotSelection.NAME, action, selection);
    }
}

export class UpdateRegulationOutlookSelection extends UpdateSelection<MonitoredRegulationOutlook> {
    static NAME = 'UpdateRegulationOutlookSelection';

    constructor(action: 'add' | 'replace', selection: Array<MonitoredRegulationOutlook>) {
        super(UpdateRegulationOutlookSelection.NAME, action, selection);
    }
}

export class UpdateTvSelection extends UpdateSelection<ExtendedMonitoredTv | string> {
    static NAME = 'UpdateTvSelection';

    constructor(action: 'add' | 'replace', selection: Array<ExtendedMonitoredTv | string>) {
        super(UpdateTvSelection.NAME, action, selection);
    }
}

export class OpenMcdmMeasure extends BusEvent {
    static NAME = 'OpenMcdmMeasure';
    measures: MonitoredMcdmMeasure[];

    constructor(measures: MonitoredMcdmMeasure[]) {
        super(OpenMcdmMeasure.NAME);
        this.measures = measures;
    }

    static fireEvent(measures: MonitoredMcdmMeasure[]) {
        AppContext.eventBus.publish(new OpenMcdmMeasure(measures));
    }
}

export class UpdateMcdmMeasureProposals extends BusEvent {
    static NAME = 'UpdateMcdmMeasureProposals';
    proposals: MonitoredMcdmMeasure[];

    constructor(measures: MonitoredMcdmMeasure[]) {
        super(UpdateMcdmMeasureProposals.NAME);
        this.proposals = measures;
    }

    static fireEvent(measures: MonitoredMcdmMeasure[]) {
        AppContext.eventBus.publish(new UpdateMcdmMeasureProposals(measures));
    }
}

export class UpdateMapCurrentTimeEvent extends BusEvent {
    static NAME = 'UpdateMapCurrentTimeEvent';
    deltaInMinutes: number;
    constructor(deltaInMinutes: number) {
        super(UpdateMapCurrentTimeEvent.NAME);
        this.deltaInMinutes = deltaInMinutes;
    }

    static fire(deltaInMinutes: number) {
        AppContext.eventBus.publish(new UpdateMapCurrentTimeEvent(deltaInMinutes));
    }
}

export class ShowMapLabelPopupEvent extends BusEvent {
    static NAME = 'ShowMapLabelPopupEvent';
    content: string;
    clientX: number;
    clientY: number;
    constructor(content, clientX: number, clientY: number) {
        super(ShowMapLabelPopupEvent.NAME);
        this.content = content;
        this.clientX = clientX;
        this.clientY = clientY;
    }

    static fire(content: string, clientX: number, clientY: number) {
        AppContext.eventBus.publish(new ShowMapLabelPopupEvent(content, clientX, clientY));
    }
}
export class MonitoringPeriodSettingsChange extends BusEvent {
    static NAME = 'MonitoringPeriodSettingsChange';
    monitoringPeriodSettings: MonitoringPeriodSettings;

    constructor(monitoringPeriodSettings: MonitoringPeriodSettings) {
        super(MonitoringPeriodSettingsChange.NAME);
        this.monitoringPeriodSettings = monitoringPeriodSettings;
    }

    static fireEvent(monitoringPeriodSettings: MonitoringPeriodSettings) {
        AppContext.eventBus.publish(new MonitoringPeriodSettingsChange(monitoringPeriodSettings));
    }
}

export class DashboardSettingsUpdateEvent extends BusEvent {
    static NAME = 'DashboardSettingsUpdateEvent';
    dashboardSettings: DashboardSettings;

    constructor(dashboardSettings: DashboardSettings) {
        super(DashboardSettingsUpdateEvent.NAME);
        this.dashboardSettings = dashboardSettings;
    }

    static fireEvent(dashboardSettings: DashboardSettings) {
        AppContext.eventBus.publish(new DashboardSettingsUpdateEvent(dashboardSettings));
    }
}

export class CoordinationSummaryUpdateEvent extends BusEvent {
    static NAME = 'CoordinationUpdateEvent';
    coordinationSummary: CoordinationSummary;

    constructor(coordinationSummary: CoordinationSummary) {
        super(CoordinationSummaryUpdateEvent.NAME);
        this.coordinationSummary = coordinationSummary;
    }

    static fireEvent(coordinationSummary: CoordinationSummary) {
        AppContext.eventBus.publish(new CoordinationSummaryUpdateEvent(coordinationSummary));
    }
}

export class PartialDashboardDataUpdateEvent extends BusEvent {
    static NAME = 'PartialDashboardDataUpdateEvent';
    entityType: EntityType;

    constructor(entityType: EntityType) {
        super(PartialDashboardDataUpdateEvent.NAME);
        this.entityType = entityType;
    }

    static fireEvent(entityType: EntityType) {
        AppContext.eventBus.publish(new PartialDashboardDataUpdateEvent(entityType));
    }
}

export class HideMapLabelPopupEvent extends BusEvent {
    static NAME = 'HideMapLabelPopupEvent';
    constructor() {
        super(HideMapLabelPopupEvent.NAME);
    }

    static fire() {
        AppContext.eventBus.publish(new HideMapLabelPopupEvent());
    }
}

export class OpenTvMonitoredEvents extends BusEvent {
    static NAME = 'OpenMonitoredEvent';
    eventType: MonitoredEventType;
    tvId: string;
    refLoc: TvRefLocation;
    tvSet: string[];
    measureIds: string[];

    constructor(type: MonitoredEventType, tvId: string, refLoc: TvRefLocation, tvSet: string[], measureIds?: string[]) {
        super(OpenTvMonitoredEvents.NAME);
        this.eventType = type;
        this.tvId = tvId;
        this.refLoc = refLoc;
        this.tvSet = tvSet;
        this.measureIds = measureIds;
    }

    static fire(
        eventType: MonitoredEventType,
        tvId: string,
        refLoc: TvRefLocation,
        tvSet?: string[],
        measureIds?: string[],
    ) {
        AppContext.eventBus.publish(new OpenTvMonitoredEvents(eventType, tvId, refLoc, tvSet, measureIds));
    }
}

export class UnityMessageOutgoingEvent extends BusEvent {
    public static NAME = 'UnityMessageOutgoingEvent';
    public request: string;

    public constructor(request: string) {
        super(UnityMessageOutgoingEvent.NAME);
        this.request = request;
    }

    public static fire(request: string) {
        const event = new UnityMessageOutgoingEvent(request);
        AppContext.eventBus.publish(event);
    }
}

export class UnityMessageIncomingEvent extends BusEvent {
    public static NAME = 'UnityMessageIncomingEvent';
    public message: string;

    public constructor(message: string) {
        super(UnityMessageIncomingEvent.NAME);
        this.message = message;
    }

    public static fire(message: string) {
        const event = new UnityMessageIncomingEvent(message);
        AppContext.eventBus.publish(event);
    }
}
